import React, { useState, useEffect, useContext } from "react";
import moment from "moment";
import _ from "lodash";
import { toast } from "react-toastify";
import http from "../services/http";
import Input from "./common/input";
import Select from "./common/select";
import DataModal from "./manageDashboardModal";
import LayoutModal from "./manageDashboardLayoutModal";
import SidebarContext from "../context/sidebarContext";
import UserContext from "../context/userContext";

const formats = [
  { id: 1, description: "Desktop" },
  { id: 2, description: "Mobile" },
];

const defaultChartLayoutData = {
  column: 1,
  row: 1,
  parameter: "",
  type: "area",
  min: "",
  max: "",
  target: "",
  height: "",
};

const defaultTableLayoutData = {
  column: 1,
  row: 1,
  parameters: [],
};

const ManageDashboard = ({ match }) => {
  const [sidebar] = useContext(SidebarContext);
  const [user] = useContext(UserContext);

  const [dashboard, setDashboard] = useState({});
  const [parameters, setParameters] = useState([]);
  const [layout, setLayout] = useState([]);
  const [code, setCode] = useState("");
  const [targetCode, setTargetCode] = useState("");
  const [label, setLabel] = useState("");
  const [history, setHistory] = useState(7);
  const [goal, setGoal] = useState("");
  const [site, setSite] = useState("");

  const [query, setQuery] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [data, setData] = useState({ HISTORY: "", LABEL: "" });

  const [showLayoutModal, setShowLayoutModal] = useState(false);
  const [layoutType, setLayoutType] = useState("");
  const [layoutComponent, setLayoutComponent] = useState({ id: 1 });
  const [layoutData, setLayoutData] = useState(defaultChartLayoutData);

  const goals = [
    { id: "Min", description: "Min" },
    { id: "Max", description: "Max" },
  ];

  const sites = [
    { id: "BA", name: "Bardney" },
    { id: "BU", name: "Bury" },
    { id: "CA", name: "Cantley" },
    { id: "NE", name: "Newark" },
    { id: "WI", name: "Wissington" },
  ];

  useEffect(() => {
    document.title = "FTR - Manage Dashboard";
    return () => {
      document.title = "FTR";
    };
  }, []);

  useEffect(() => {
    async function getDashboard() {
      try {
        const { data } = await http.get(`/dashboard/${match.params.id}`);
        setDashboard(data);
      }
      catch {
        //ignore error
      }
    }

    async function getParameters() {
      try {
        const { data } = await http.get(`/dashboard-parameters/${match.params.id}`);
        setParameters(data);
      }
      catch {
        //ignore error
      }
    }
    getDashboard();
    getParameters();
  }, [match.params.id]);

  useEffect(() => {
    async function getLayout() {
      try {
        const { data } = await http.get(`/dashboard-layout/rows/${dashboard.ID}`);
        setLayout(data);
      }
      catch {
        //ignore error
      }
    }
    
    if (dashboard.MANAGE_TYPE === 2) {
      getLayout();
    }
  }, [dashboard]);

  async function handleSave(e) {
    e.preventDefault();

    const payload = {
      description: dashboard.DESCRIPTION,
      format: dashboard.FORMAT_TYPE,
    };
    try {
      await http.patch(`/dashboard/${match.params.id}`, payload);
      toast.success("Dashboard Updated");
    } catch (ex) {
      toast.error("Oh Lawd");
    }
  }

  async function handleRemove(parm) {
    const oldParameters = [...parameters];

    try {
      await http.delete(`/dashboard-parameters/${parm.ID}`);
      setParameters(parameters.filter((p) => p.PARM_CODE !== parm.PARM_CODE));
      toast.success(`Parameter ${parm.code} removed`);
    } catch (ex) {
      if (ex.response && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
      setParameters(oldParameters);
    }
  }

  async function handleAdd(e) {
    e.preventDefault();

    const payload = {
      code,
      targetCode,
      history,
      label,
      goal,
      site,
    };
    try {
      const { data } = await http.post(
        `/dashboard-parameters/${match.params.id}`,
        payload
      );
      setParameters([...parameters, data]);
      toast.success("Parameter Added");
    } catch (ex) {
      if (ex.response && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
  }

  function handleEdit(parm) {
    setData(parm);
    setShowModal(true);
  }

  function handleModalChange({ currentTarget: input }) {
    const newData = { ...data };
    newData[input.name] = input.value;
    setData(newData);
  }

  function handleClose() {
    setShowModal(false);
    setData({ history: "", label: "" });
  }

  async function handleModalSubmit(e) {
    e.preventDefault();

    const payload = {
      target: data.TARGET_CODE,
      history: data.HISTORY,
      label: data.LABEL,
      goal: data.GOAL,
      site: data.SITE,
    };
    try {
      const { data: parm } = await http.patch(
        `/dashboard-parameters/${data.ID}`,
        payload
      );
      const newParmeters = [...parameters];
      const index = parameters.map((p) => p.ID).indexOf(data.ID);
      newParmeters[index] = parm;
      setParameters(newParmeters);
      handleClose();
      toast.success("Parameter Updated");
    } catch (ex) {
      if (ex.response && ex.response.status <= 500) {
        toast.error(ex.response.data);
      }
    }
  }

  const handleLayoutAdd = () => {
    setLayoutType("Add");
    setLayoutData(defaultChartLayoutData);
    setShowLayoutModal(true);
  };

  const handleCloseLayout = () => {
    setShowLayoutModal(false);
  };

  const handleComponentChange = (item) => {
    setLayoutComponent(item);
    if (item.id === 1) {
      setLayoutData(defaultChartLayoutData);
    } else {
      setLayoutData(defaultTableLayoutData);
    }
  };

  const handleLayoutChange = ({ currentTarget: input }) => {
    const newLayout = { ...layoutData };
    newLayout[input.name] = input.value;
    setLayoutData(newLayout);
  };

  const handleLayoutSubmit = async (e) => {
    e.preventDefault();

    const omitList = ["row", "column"];
    if (!layoutData.min) omitList.push("min");
    if (!layoutData.max) omitList.push("max");

    const payload = {
      row: layoutData.row,
      column: layoutData.column,
      component: layoutComponent.id,
      config: _.omit(layoutData, omitList),
    };

    if (layoutData.parameters && layoutData.parameters.length === 0)
      return toast.error("invalid Parameter List");

    try {
      const { data } = await http.post(
        `/dashboard-layout/${match.params.id}`,
        payload
      );
      setLayout((currentLayout) => [...currentLayout, data]);
      setShowLayoutModal(false);
    } catch (ex) {
      if (ex.response && ex.response.status <= 500) {
        toast.error(ex.response.data);
      }
    }
  };

  const handleLayoutDelete = async (item) => {
    const oldLayout = [...layout];
    setLayout(layout.filter((l) => l.ID !== item.ID));

    try {
      await http.delete(`/dashboard-layout/${item.ID}`);
      toast.success("Item removed");
    } catch (ex) {
      setLayout(oldLayout);
      if (ex.response && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
  };

  const handleLayoutParameterAdd = (code) => {
    if (code === "") return;
    const parameterList = [...layoutData.parameters, code];
    setLayoutData((data) => {
      return { ...data, parameters: parameterList };
    });
  };

  async function handleSequenceUpdate(direction, parameter, index) {
    if (direction === "UP" && index === 0) return;
    if (direction === "DOWN" && index === parameters.length - 1) return;
    let offset = 0;
    if (direction === "UP") offset -= 1;
    if (direction === "DOWN") offset += 1;

    const Id = parameter.ID;
    const offsetId = parameters[index + offset].ID;

    try {
      await http.patch(`/dashboard-parameters/sequence/${Id}/${offsetId}`);
      // Always update after database
      // Stops users from submitting another change without the previous one being complete
      const newParameters = [...parameters];
      const temp = newParameters[index + offset];
      const tempSeq = newParameters[index + offset].SEQ_NO;
      newParameters[index + offset] = newParameters[index];
      newParameters[index] = temp;
      // Swap Sequence Numbers here too
      newParameters[index].SEQ_NO = newParameters[index + offset].SEQ_NO;
      newParameters[index + offset].SEQ_NO = tempSeq;
      setParameters(newParameters);
    } catch (ex) {
      console.log(ex);
    }
  }

  let filtered = parameters;
  if (query)
    filtered = parameters.filter(
      (p) =>
        p.PARM_CODE.includes(query.toUpperCase()) ||
        p.LABEL.toLowerCase().includes(query.toLowerCase())
    );

  return (
    <div className={sidebar ? "col-10 mt-4" : "col-12 mt-4"}>
      <form className="container">
        <div className="form-row">
          <div className="col-xl-6">
            <Input
              name="DESCRIPTION"
              label="Dashboard Name"
              placeholder="name"
              value={dashboard.DESCRIPTION}
              onChange={(e) =>
                setDashboard({
                  ...dashboard,
                  DESCRIPTION: e.currentTarget.value,
                })
              }
            />
          </div>
          <div className="col-xl-6">
            <Select
              name="FORMAT_TYPE"
              label="Format"
              placeholder="Select Format"
              options={formats}
              value={dashboard.FORMAT_TYPE}
              onChange={(e) =>
                setDashboard({
                  ...dashboard,
                  FORMAT_TYPE: e.currentTarget.value,
                })
              }
              listKey={"id"}
              listName={"description"}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="col-auto">
            <button className="btn btn-primary" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </form>
      <hr className="bg-success container" />
      <form className="container">
        <div className="form-row">
          <div className="col">
            <Input
              name="code"
              label="Parameter"
              placeholder="Code"
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
            />
          </div>
          <div className="col">
            <Input
              name="targetCode"
              label="Target Code"
              placeholder="Code"
              value={targetCode}
              onChange={(e) => setTargetCode(e.currentTarget.value)}
            />
          </div>
          <div className="col">
            <Input
              name="label"
              label="Label"
              placeholder="Label"
              value={label}
              onChange={(e) => setLabel(e.currentTarget.value)}
            />
          </div>
          <div className="col-2">
            <Input
              name="history"
              label="History (days)"
              placeholder="days"
              value={history}
              onChange={(e) => setHistory(e.currentTarget.value)}
            />
          </div>
          <div className="col-2">
            <Select
              name="goal"
              options={[{ id: "" }, ...goals]}
              label="Goal"
              placeholder="Goal"
              value={goal}
              onChange={(e) => setGoal(e.currentTarget.value)}
              listKey="id"
              listName="description"
            />
          </div>
          {user && dashboard && user.SITE === "CO" && dashboard.TYPE === "C" && (
            <div className="col-2">
              <Select
                name="site"
                options={[{ id: "" }, ...sites]}
                label="Site"
                placeholder="Site"
                value={site}
                onChange={(e) => setSite(e.currentTarget.value)}
                listKey="id"
                listName="name"
              />
            </div>
          )}
        </div>
        <div className="form-row justify-content-end">
          <button className="btn btn-primary" onClick={handleAdd}>
            Add
          </button>
        </div>
      </form>
      <hr className="bg-success container" />
      <div className="row mt-3">
        <div className="col">
          <Input
            name="search"
            placeholder="Search Dashboard..."
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
        </div>
      </div>
      <table className="table table-sm">
        <caption style={{ captionSide: "top", paddingBottom: 0 }}>
          {parameters.length} Parameters
        </caption>
        <thead>
          <tr>
            <th>Parameter</th>
            <th>Target</th>
            <th>History</th>
            <th>Label</th>
            <th>Goal</th>
            <th>Site</th>
            <th>Last Updated</th>
            <th>Updated By</th>
            <th />
            <th />
            <th />
          </tr>
        </thead>
        <tbody>
          {filtered.map((parm, index) => (
            <tr key={parm.ID}>
              <td>
                <button
                  className="btn btn-link"
                  onClick={() => handleEdit(parm)}
                >
                  {parm.PARM_CODE}
                </button>
              </td>
              <td>{parm.TARGET_CODE}</td>
              <td>{parm.HISTORY}</td>
              <td>{parm.LABEL}</td>
              <td>{parm.GOAL}</td>
              <td>{parm.LONG_SITE}</td>
              <td>{moment(parm.LAST_UPDATED_DTS).fromNow()}</td>
              <td>{parm.LAST_UPDATED_BY}</td>
              <td>
                <button
                  className="btn btn-sm btn-success mr-1"
                  onClick={() => handleSequenceUpdate("UP", parm, index)}
                >
                  <i className="fas fa-arrow-up"></i>
                </button>
                <button
                  className="btn btn-sm btn-success ml-1"
                  onClick={() => handleSequenceUpdate("DOWN", parm, index)}
                >
                  <i className="fas fa-arrow-down"></i>
                </button>
              </td>
              <td>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleRemove(parm)}
                >
                  <i className="fas fa-trash"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {dashboard && dashboard.MANAGE_TYPE === 2 && (
        <React.Fragment>
          <hr className="bg-success container" />
          <h3>Layout</h3>
          <button className="btn btn-primary" onClick={handleLayoutAdd}>
            Add
          </button>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Component</th>
                <th>Row</th>
                <th>Column</th>
                <th>Config</th>
                <th>Last Updated</th>
                <th>Updated By</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {layout.map((record) => (
                <tr key={record.ID}>
                  <td>{record.COMPONENT}</td>
                  <td>{record.ROW_NO}</td>
                  <td>{record.COL_NO}</td>
                  <td>{record.CONFIG}</td>
                  <td>{moment(record.LAST_UPDATED_DTS).fromNow()}</td>
                  <td>{record.LAST_UPDATED_BY}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleLayoutDelete(record)}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </React.Fragment>
      )}
      <DataModal
        show={showModal}
        data={data}
        handleChange={handleModalChange}
        handleClick={handleModalSubmit}
        handleClose={handleClose}
      />
      <LayoutModal
        show={showLayoutModal}
        layoutComponent={layoutComponent}
        layoutConfig={layoutData}
        layoutType={layoutType}
        handleComponentChange={handleComponentChange}
        handleChange={handleLayoutChange}
        handleClose={handleCloseLayout}
        handleSubmit={handleLayoutSubmit}
        parameters={parameters}
        handleAdd={handleLayoutParameterAdd}
      />
    </div>
  );
};

export default ManageDashboard;
